import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Modal from '@/components/Modal'
import { Field } from '@/components/Field'
import { supabase } from '@/lib/supabase'
import { useSaveDb, ensureSaveDb } from '@/lib/useSaveDb'
import { withTimeout } from '@/lib/saveTimeout'
import { throwFriendly } from '@/lib/friendlyError'
import { ValidationError } from '@/lib/errorReporter'
import { RUN_FRUIT_DOMAIN } from '@/lib/packhouseErrorDomains'
import { groupVarietiesByFruit, fruitLabel, type VarietyGroup } from '@/lib/runVarietyGroups'
import { useGrowerTree } from '@/hooks/useGrowerData'
import { useCloseAfterTick } from '@/modules/farm/useCloseAfterTick'
import type { Run } from '@/types/database'

// ui-flow F21 UF-07. A run takes its fruit from the variety it was started on;
// an empty run started on the wrong block offers only that fruit's pack types.
// correct_run_variety (mig 20261090000000) re-points it while nothing is tipped
// or packed. The server is the boundary: canChangeRunFruit only hides the button.

/** The run fields the modal reads. */
export type ChangeFruitRun = Pick<Run, 'run_id' | 'run_number' | 'grower' | 'variety'> & { grower_id?: string | null }

const MIN_REASON = 3

export default function ChangeRunFruitModal({ run, onClose }: { run: ChangeFruitRun; onClose: () => void }) {
  const qc = useQueryClient()
  const saveDb = useSaveDb()
  const treeQ = useGrowerTree()
  const [variety, setVariety] = useState('')
  const [reason, setReason] = useState('')
  // Asked BEFORE mutate(): a mutation that throws before it ever shows "pending"
  // gives the save overlay nothing to report, so the refusal would be invisible.
  const [reasonTooShort, setReasonTooShort] = useState(false)
  const tick = useCloseAfterTick(onClose)

  // The same source New Run uses: the grower's own blocks, grouped by fruit.
  const groups = useMemo<VarietyGroup[]>(() => {
    const tree = (treeQ.data ?? []) as {
      grower_id: string; name: string
      pucs?: { blocks?: { variety: string | null; commodity: string | null }[] }[]
    }[]
    const g = tree.find(x => run.grower_id ? x.grower_id === run.grower_id : x.name === run.grower)
    return groupVarietiesByFruit((g?.pucs ?? []).flatMap(p => p.blocks ?? []))
  }, [treeQ.data, run.grower_id, run.grower])
  const optionCount = groups.reduce((n, g) => n + g.varieties.length, 0)
  const freeText = !treeQ.isPending && optionCount === 0

  const isNow = (v: string) => v.trim().toLowerCase() === run.variety.trim().toLowerCase()
  const picked = variety.trim()
  const pickedFruit = fruitLabel(groups.find(g => g.varieties.includes(picked))?.fruit)
  const submitLabel = pickedFruit && !isNow(picked) ? `Change to ${pickedFruit}` : 'Change fruit'

  const save = useMutation({
    mutationFn: async () => {
      if (!picked || isNow(picked)) throw new ValidationError('Pick a variety other than the one this run is on.')
      const db = (saveDb ?? await ensureSaveDb()) ?? supabase
      const { error } = await withTimeout(
        db.rpc('correct_run_variety', { p_run_id: run.run_id, p_variety: picked, p_reason: reason.trim() }),
        'ChangeRunFruit: correct_run_variety',
      )
      if (error) throwFriendly(error, RUN_FRUIT_DOMAIN)
    },
    onSuccess: () => {
      // ['runs', …] is the register, the run detail (by-id) and the packing /
      // pallet pickers (['runs','selection','packing','pallets']), so the New
      // Pallet form sees the new fruit on its next render.
      qc.invalidateQueries({ queryKey: ['runs'] })
      qc.invalidateQueries({ queryKey: ['ready-to-tip'] })
      tick.armClose()
    },
  })

  return (
    <Modal
      title="Change this run's fruit"
      subtitle={`${run.run_number} · ${run.grower} · ${run.variety}`}
      onClose={onClose}
      snapshot={JSON.stringify([variety, reason])}
      onSubmit={() => {
        if (reason.trim().length < MIN_REASON) { setReasonTooShort(true); return }
        tick.guardSubmit(() => save.mutate())
      }}
      isPending={save.isPending || tick.closing}
      submitDisabled={!!picked && isNow(picked)}
      submitLabel={submitLabel}
      submitPendingLabel="Changing…"
      size="md"
      overlay={{
        isPending: save.isPending, isSuccess: save.isSuccess, isError: save.isError, error: save.error,
        domain: RUN_FRUIT_DOMAIN,
        pendingLabel: 'Changing the fruit…', successLabel: 'Fruit changed',
        onDismiss: tick.closeAfterTick,
      }}>
      <div className="space-y-4">
        <Field label="Variety" required>
          <VarietyPicker groups={groups} freeText={freeText} value={variety} onChange={setVariety} isNow={isNow} />
        </Field>
        <Field label="Reason" required>
          <input className="field" required value={reason} maxLength={200} autoComplete="off"
            placeholder="For example: started on the wrong block"
            onChange={e => { setReason(e.target.value); setReasonTooShort(false) }} />
          {reasonTooShort && (
            <span role="alert" className="block mt-1 text-caption font-semibold text-danger">
              Give a reason for the change (a few words).
            </span>
          )}
        </Field>
        <p className="text-body text-fg-secondary">
          Only while nothing is tipped or packed. The run keeps its number, and the change is kept in the run&apos;s history.
        </p>
      </div>
    </Modal>
  )
}

/** The run's grower's varieties grouped by fruit, the current one disabled; free text when the grower has no blocks. */
function VarietyPicker({ groups, freeText, value, onChange, isNow }: {
  groups: VarietyGroup[]; freeText: boolean; value: string; onChange: (v: string) => void; isNow: (v: string) => boolean
}) {
  if (freeText) {
    return (
      <input className="field" aria-label="Variety" required value={value} maxLength={80} autoComplete="off"
        placeholder="Type the variety" onChange={e => onChange(e.target.value)} />
    )
  }
  return (
    <select className="field" aria-label="Variety" required value={value} onChange={e => onChange(e.target.value)}>
      <option value="">Pick a variety</option>
      {groups.map(g => (
        <optgroup key={g.fruit ?? 'other'} label={fruitLabel(g.fruit) ?? 'Other'}>
          {g.varieties.map(v => <option key={v} value={v} disabled={isNow(v)}>{isNow(v) ? `${v} (now)` : v}</option>)}
        </optgroup>
      ))}
    </select>
  )
}
